import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { AuthContext } from '../App';
import GatewayHealthIndicator from './GatewayHealthIndicator';
import {
  FileText, Package, ShoppingCart, ShoppingBag, Contact, DollarSign, MessageSquare, Users, Settings, LogOut, Palette, Inbox,
  Image as ImageIcon, BookOpen, Hammer, BarChart3, Sparkles, Mail, Building2, Store, LayoutDashboard, Briefcase, Factory, Layers, SlidersHorizontal, PlusCircle, Receipt, UserCircle } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { NavMenu } from './nav/NavMenu';
import { MobileNav, PlainLink } from './nav/MobileNav';

const AppHeader = () => {
  const { user, logout } = useContext(AuthContext);
  const feat = (n) => user?.company?.features?.[n] !== false;
  const isAdmin = user?.role === 'admin';
  const location = useLocation();
  const navigate = useNavigate();
  const [counts, setCounts] = useState({ purchasing: 0, orders: 0, proposals: 0, leads: 0, quoteRequests: 0 });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const [purchasingRes, ordersRes, proposalsRes, leadsRes, qrRes] = await Promise.all([
          axios.get('/purchasing/stats').catch(() => ({ data: {} })),
          axios.get('/orders/stats/summary').catch(() => ({ data: {} })),
          axios.get('/proposals/stats').catch(() => ({ data: {} })),
          axios.get('/leads').catch(() => ({ data: [] })),
          user?.role === 'admin' ? axios.get('/admin/quote-requests', { params: { status: 'new' } }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        ]);
        setCounts({
          purchasing: purchasingRes.data.needs_purchasing || 0,
          orders: purchasingRes.data.pending || ordersRes.data.pending || 0,
          quoteRequests: qrRes.data?.total || qrRes.data?.counts?.new || 0,
          proposals: proposalsRes.data.unpaid || 0,
          leads: (Array.isArray(leadsRes.data) ? leadsRes.data : []).filter((l) => (l.status || 'new') === 'new').length,
        });
      } catch (error) { console.log('Could not fetch badge counts'); }
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line

  const onProposals = (loc) => loc.pathname.startsWith('/proposal') || loc.pathname === '/quote-builder' || (loc.pathname === '/' && loc.search.includes('filter='));
  const dashboard = { label: 'Dashboard', to: '/', icon: LayoutDashboard, testId: 'nav-dashboard', isActive: (loc) => loc.pathname === '/' && !loc.search.includes('filter=') };

  // Same pages, routes and permissions as before — only grouped. `feat()` / role checks are unchanged.
  const groups = useMemo(() => [
    { id: 'sales', label: 'Sales', icon: Briefcase, items: [
      feat('leads_inbox') && { label: 'Leads', to: '/leads', icon: Inbox, testId: 'nav-leads', badge: { count: counts.leads, color: '#10b981', testId: 'leads-badge', title: 'New leads' } },
      { label: 'Contacts', to: '/contacts', icon: Contact, testId: 'nav-contacts' },
      { label: 'Proposals', to: '/?filter=all', icon: FileText, testId: 'nav-proposals', isActive: onProposals, badge: { count: counts.proposals, color: '#f59e0b', testId: 'proposals-badge', title: 'Click to view unpaid proposals', to: '/?filter=unpaid' } },
      isAdmin && { label: 'Quote Requests', to: '/quote-requests', icon: Inbox, testId: 'nav-quote-requests', badge: { count: counts.quoteRequests, color: '#dc2626', testId: 'nav-quote-requests-badge', title: 'New quote requests' } },
      { label: 'Pricing', to: '/price-matrices', icon: DollarSign, testId: 'nav-pricing' },
    ].filter(Boolean) },
    { id: 'orders', label: 'Orders', icon: Package, items: [
      { label: 'Orders', to: '/orders', icon: Package, testId: 'nav-orders', badge: { count: counts.orders, color: '#3b82f6', testId: 'orders-badge', title: 'Click to view pending orders', to: '/orders?filter=pending' } },
      { label: 'Purchasing', to: '/purchasing', icon: ShoppingCart, testId: 'nav-purchasing', badge: { count: counts.purchasing, color: '#ef4444', testId: 'purchasing-badge', title: 'Click to view items needing purchase', to: '/purchasing?tab=needs_purchasing' } },
      isAdmin && feat('past_work_gallery') && { label: 'Past Work', to: '/past-work', icon: ImageIcon, testId: 'nav-past-work' },
    ].filter(Boolean) },
    { id: 'production', label: 'Production', icon: Factory, items: [
      { label: 'Production Queue', to: '/production', icon: Hammer, testId: 'nav-production' },
      { label: 'Reports', to: '/reports/completed', icon: BarChart3, testId: 'nav-reports' },
    ] },
    { id: 'stores', label: 'Stores', icon: Store, items: [
      { label: 'All Stores', to: '/stores', icon: Store, testId: 'nav-stores', isActive: (loc) => loc.pathname.startsWith('/stores') && !loc.search.includes('new=1') && !loc.search.includes('view=orders') && !/^\/stores\/[^/]+\/orders/.test(loc.pathname) },
      isAdmin && { label: 'Create Store', to: '/stores?new=1', icon: PlusCircle, testId: 'nav-stores-create', isActive: (loc) => loc.pathname === '/stores' && loc.search.includes('new=1') },
      { label: 'Store Orders', to: '/stores?view=orders', icon: Receipt, testId: 'nav-stores-orders', isActive: (loc) => (loc.pathname === '/stores' && loc.search.includes('view=orders')) || /^\/stores\/[^/]+\/orders/.test(loc.pathname) },
    ].filter(Boolean) },
    { id: 'design', label: 'Design & Products', short: 'Design', icon: Layers, items: [
      feat('design_studio') && { label: 'Design Studio', to: '/design-studio', icon: Palette, testId: 'nav-design' },
      { label: 'Designs', to: '/designs', icon: Sparkles, testId: 'nav-designs' },
      { label: 'Products', to: '/products', icon: ShoppingBag, testId: 'nav-products' },
      isAdmin && { label: 'Catalog', to: '/internal-catalog', icon: BookOpen, testId: 'nav-internal-catalog' },
    ].filter(Boolean) },
    { id: 'management', label: 'Management', icon: SlidersHorizontal, items: [
      feat('ai_assistant') && { label: 'AI Assistant', to: '/ai-assistant', icon: MessageSquare, testId: 'nav-ai' },
      isAdmin && { label: 'Company Settings', to: '/settings', icon: Building2, testId: 'nav-company-settings' },
      isAdmin && { label: 'Communications', to: '/communications', icon: Mail, testId: 'nav-communications' },
      isAdmin && { label: 'Users', to: '/users', icon: Users, testId: 'nav-users' },
      { label: 'My Profile', to: '/profile', icon: UserCircle, testId: 'nav-profile' },
    ].filter(Boolean) },
  ].filter((g) => g.items.length), [counts, user]); // eslint-disable-line

  const itemActive = (it) => (it.isActive ? it.isActive(location) : (location.pathname === (it.to || '').split('?')[0] || (it.to && it.to !== '/' && location.pathname.startsWith(it.to.split('?')[0]))));
  const isGroupActive = (g) => g.items.some(itemActive);
  const badgeTotal = (g) => g.items.reduce((a, it) => a + (it.badge?.count || 0), 0);

  const userMenu = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {isAdmin && <GatewayHealthIndicator />}
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#e74c3c', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 600 }}>{user?.name?.charAt(0).toUpperCase() || 'A'}</div>
      <span className="dw-nav-user-name" style={{ fontWeight: 500, color: '#fff', fontSize: 14 }}>{user?.name || 'Admin User'}</span>
      <Button variant="ghost" size="sm" onClick={() => navigate('/profile')} style={{ color: '#fff' }} title="Profile" data-testid="nav-profile-btn"><Settings size={18} /></Button>
      <Button variant="ghost" size="sm" onClick={logout} style={{ color: '#fff' }} title="Log out" data-testid="nav-logout"><LogOut size={18} /></Button>
    </div>
  );

  return (
    <header className="dw-header" data-testid="app-header" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 16, position: 'sticky', top: 0, zIndex: 50, boxShadow: '0 2px 12px rgba(0,0,0,0.35)' }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }} data-testid="nav-logo"><BrandLogo height={38} light style={{ cursor: 'pointer' }} /></Link>
      <nav className="dw-nav-desktop" aria-label="Main" style={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }} data-testid="main-nav">
        <PlainLink item={dashboard} active={itemActive(dashboard)} />
        {groups.map((g) => <NavMenu key={g.id} group={g} active={isGroupActive(g)} badgeTotal={badgeTotal(g)} />)}
      </nav>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div className="dw-nav-desktop">{userMenu}</div>
        <MobileNav groups={groups} dashboard={dashboard} isGroupActive={isGroupActive} badgeTotal={badgeTotal} footer={userMenu} />
      </div>
    </header>
  );
};

export default AppHeader;
